import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { inviteMemberInput } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useBarbers } from '@/lib/org';
import { useRoles } from '../catalog/api';
import { applyServerError, zodForm } from '../catalog/lib';

interface Values {
  email: string;
  roleId: string;
  barberId: string;
}
interface InviteResult {
  id: string;
  emailStatus: string;
  inviteUrl: string | null;
}
const NONE = 'none';

export function InviteModal({
  open,
  onOpenChange,
  defaultRoleKey,
  defaultBarberId,
  lockBarber,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultRoleKey?: string;
  defaultBarberId?: string;
  lockBarber?: boolean;
}) {
  const qc = useQueryClient();
  const roles = useRoles();
  const barbers = useBarbers();
  const [result, setResult] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const norm = (v: Values) => ({ email: v.email, roleId: v.roleId, barberId: v.barberId === NONE ? null : v.barberId });
  const form = useForm<Values>({ resolver: zodForm<Values>(inviteMemberInput, norm), defaultValues: { email: '', roleId: '', barberId: NONE } });
  const { errors } = form.formState;

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setCopied(false);
    const role = roles.data?.find((r) => r.key === defaultRoleKey) ?? roles.data?.find((r) => r.key === 'receptionist');
    form.reset({ email: '', roleId: role?.id ?? '', barberId: defaultBarberId ?? NONE });
  }, [open, roles.data, defaultRoleKey, defaultBarberId]); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = useMutation({
    mutationFn: (v: Values) => api.post<InviteResult>('/org/invitations', inviteMemberInput.parse(norm(v))),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['org', 'invitations'] });
      if (r.inviteUrl) setResult(r);
      else {
        toast.success('Convite enviado');
        onOpenChange(false);
      }
    },
    onError: (e) => applyServerError(form, e),
  });

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copiado');
    } catch {
      toast.error('Não foi possível copiar. Selecione o link e copie manualmente.');
    }
  }

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={result ? 'Convite criado' : 'Enviar convite'}
      description={result ? undefined : 'A pessoa recebe um link para criar a senha e entrar com o papel escolhido.'}
      size="md"
      footer={
        result ? (
          <Button onClick={() => onOpenChange(false)}>Concluir</Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Voltar
            </Button>
            <Button type="submit" form="invite-form" disabled={invite.isPending}>
              {invite.isPending ? 'Enviando…' : 'Enviar convite'}
            </Button>
          </>
        )
      }
    >
      {result?.inviteUrl ? (
        <div className="grid gap-4">
          <div role="alert" className="flex gap-3 rounded-md border border-brass/50 bg-brass/10 p-3 text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-ink" aria-hidden />
            <p>
              <strong>O e-mail não foi enviado</strong>, porque o envio de e-mails não está configurado. Copie o link abaixo e mande para a pessoa. Ele aparece só agora e vale por 7 dias.
            </p>
          </div>
          <Field id="invite-url" label="Link do convite">
            <div className="flex gap-2">
              <Input id="invite-url" readOnly value={result.inviteUrl} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
              <Button type="button" variant="outline" onClick={() => copy(result.inviteUrl!)}>
                {copied ? <Check aria-hidden /> : <Copy aria-hidden />} {copied ? 'Copiado' : 'Copiar link'}
              </Button>
            </div>
          </Field>
        </div>
      ) : (
        <form id="invite-form" onSubmit={form.handleSubmit((v) => invite.mutate(v))} className="grid gap-4" noValidate>
          <Field id="invite-email" label="E-mail" error={errors.email?.message}>
            <Input id="invite-email" type="email" autoComplete="off" autoFocus aria-invalid={!!errors.email} {...form.register('email')} />
          </Field>
          <Field id="invite-role" label="Papel" error={errors.roleId?.message}>
            <Controller
              control={form.control}
              name="roleId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="invite-role" className="w-full">
                    <SelectValue placeholder="Escolha um papel" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.data?.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="invite-barber" label="Profissional vinculado (opcional)" hint="Quem tem papel de barbeiro vê a própria agenda e comissões.">
            <Controller
              control={form.control}
              name="barberId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={lockBarber}>
                  <SelectTrigger id="invite-barber" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Nenhum</SelectItem>
                    {barbers.data?.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </form>
      )}
    </FormModal>
  );
}
